import { Accordion, AccordionSection, Headline, Layout, Text } from '@oneaudi/unified-web-components';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useI18n } from '~/app/i18n/i18n';
import { MoreInformation } from '~/app/pages/configurator/components/more-information-dialog';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import {
	StyledEquipmentItemWrapper,
	StyledImageTeaser,
	StyledStandAloneImageTeaser,
} from '~/app/pages/summary/dialogs/standard-equipment/styles/standard-equipment.dialog-style';
import { useSelectedStandardEquipment } from '~/app/pages/summary/dialogs/standard-equipment/use-standard-equipment';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { useDebugMode } from '~/app/utils/debug-mode';
import { Family, Group } from '~/app/utils/group-items';
import placeholder from '~/assets/icons/placeholder.svg';
import { Dialog } from '~/components/dialog';
import { RenderHtml } from '~/components/text';
import { ConfiguredCarFeatureFragment } from '~/graphql/__generated__/graphql';
import { useCatalog } from '~/graphql/hooks/use-configurator';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { sanitizeText } from '~/utils/sanitize-text';

export function StandardEquipmentDialog({ open, onClose }: DialogProps) {
	useEffect(() => {
		open &&
			appTrackingService.viewChangeEvent({
				eventName: 'configurator - standard equipment layer',
				componentName: 'standard-equipment',
			});
	}, [open]);

	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<StandardEquipmentDialogContent open={open} />
		</Dialog>
	);
}

const StandardEquipmentDialogContent = ({ open }: { open: boolean }) => {
	const { i18n } = useI18n();
	const { carInfo } = useCatalog();
	const { groupedItems, isLoading } = useSelectedStandardEquipment(open);
	const [accordionOpen, setAccordionOpen] = useState<Record<number, boolean>>({});
	return (
		<>
			<Layout direction="column" rowGap="16px" columnGap="16px">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'bold' }}>
					{i18n('ngc.equipment.standard')}
				</Headline>
				<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{i18n('ngc.technicalData.carlineModel', {
						carline: carInfo?.carline,
						model: carInfo?.model,
					})}
				</Text>
			</Layout>

			{isLoading && (
				<Layout direction="column" rowGap="64px" columnGap="64px">
					<Skeleton height={256} />
					<Skeleton height={256} />
					<Skeleton height={256} />
				</Layout>
			)}

			{/* TODO: Find out what sections should be displayed */}

			<Accordion>
				{groupedItems.map((group, index) => {
					return (
						<AccordionSection
							key={group.id}
							open={accordionOpen[index]}
							onToggle={() => setAccordionOpen((prev) => ({ ...prev, [index]: !accordionOpen[index] }))}
							headingTag="h3"
							label={group.name}>
							<EquipmentGroup group={group} />
						</AccordionSection>
					);
				})}
			</Accordion>
		</>
	);
};

function EquipmentGroup({ group }: { group: Group<ConfiguredCarFeatureFragment> }) {
	return (
		<section className="flex flex-col gap-8">
			{/* <Text variant="order3" weight="bold">
				{group.name}
			</Text> */}

			<ul className="flex flex-col gap-8">
				{group.families.map((family) => (
					<EquipmentFamily key={family.id} family={family} />
				))}
			</ul>
		</section>
	);
}

function EquipmentFamily({ family }: { family: Family<ConfiguredCarFeatureFragment> }) {
	return (
		<section className="flex flex-col gap-4">
			<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{family.name}</Text>

			<ul className="flex flex-col">
				{family.items.map((item) => (
					<EquipmentItem key={item.pr7} item={item} />
				))}
			</ul>
		</section>
	);
}

function EquipmentItem({ item }: { item: ConfiguredCarFeatureFragment }) {
	const { getName } = useDebugMode();
	const sanitizedName = sanitizeText(getName(item)!);

	return (
		<StyledEquipmentItemWrapper>
			{GetUrlFromImageSet(item.imageSets, 'teaser_image') ? (
				<StyledStandAloneImageTeaser
					as="img"
					src={GetUrlFromImageSet(item.imageSets, 'teaser_image') || ''}
					alt={sanitizedName}
				/>
			) : (
				<StyledImageTeaser>
					<img src={placeholder} alt="Audi Logo Placeholder" />
				</StyledImageTeaser>
			)}

			<Layout direction="column" justify="space-between">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{<RenderHtml html={sanitizedName} />}</Text>
				<MoreInformation featureOrPackage={item} item={undefined} />
			</Layout>
		</StyledEquipmentItemWrapper>
	);
}
